/**
 * 비디오 수신 해상도 정책.
 *
 * 참여자 수(한 화면에 표시되는 타일 수)와 뷰 모드에 따라
 * JVB 브릿지에 요청할 maxHeight 값을 결정한다.
 *
 * 정책 기준 (스크린샷 기반):
 *   그리드뷰: 2개 이하 720 / 8개 이하 360 / 9개 이상 180
 *   발표자뷰: 메인 비디오 최대(720), 서브 비디오 리스트 180
 *   사이드뷰: 메인 비디오 최대(720), 서브 비디오 리스트 180
 */

// ─── 해상도 레벨 ────────────────────────────────────────────────────────────

export const VIDEO_QUALITY_LEVELS = {
  HIGH: 720,
  STANDARD: 360,
  LOW: 180,
} as const;

export type VideoQualityLevel = (typeof VIDEO_QUALITY_LEVELS)[keyof typeof VIDEO_QUALITY_LEVELS];

// ─── 그리드뷰 임계값 정책 ──────────────────────────────────────────────────

export interface GridTier {
  /** 이 수 이하일 때 해당 maxHeight 적용 */
  maxCount: number;
  maxHeight: VideoQualityLevel;
}

/**
 * 기본 그리드 정책 (오름차순).
 * 첫 번째로 visibleCount ≤ maxCount 인 항목이 적용된다.
 * 마지막 항목의 maxHeight가 "그 이상" 폴백으로 사용됨.
 */
export const DEFAULT_GRID_TIERS: readonly GridTier[] = [
  { maxCount: 2, maxHeight: VIDEO_QUALITY_LEVELS.HIGH },     // ≤2 → 720p
  { maxCount: 8, maxHeight: VIDEO_QUALITY_LEVELS.HIGH },  // ≤8 → 720p
] as const;

export const DEFAULT_GRID_FALLBACK: VideoQualityLevel = VIDEO_QUALITY_LEVELS.LOW; // 9+ → 180p

/**
 * 그리드뷰에서 현재 화면에 보이는 타일 수에 따른 수신 해상도를 반환한다.
 *
 * @param visibleCount 화면에 표시 중인 비디오 타일 수 (본인 포함)
 * @param tiers 커스텀 임계값 배열 (오름차순). 생략 시 기본 정책 사용.
 * @param fallback 마지막 임계값 초과 시 사용할 해상도. 생략 시 기본 폴백 사용.
 */
export function getGridResolution(
  visibleCount: number,
  tiers: readonly GridTier[] = DEFAULT_GRID_TIERS,
  fallback: VideoQualityLevel = DEFAULT_GRID_FALLBACK,
): VideoQualityLevel {
  for (const tier of tiers) {
    if (visibleCount <= tier.maxCount) return tier.maxHeight;
  }
  return fallback;
}

// ─── 포커스 뷰 정책 (발표자·사이드 공통) ───────────────────────────────────

export const FOCUS_VIEW_POLICY = {
  /** 메인(대화면) 비디오 maxHeight */
  main: VIDEO_QUALITY_LEVELS.HIGH,    // 720p
  /** 서브(리스트) 비디오 maxHeight */
  sub: VIDEO_QUALITY_LEVELS.HIGH,      // 720p
} as const;

// ─── 테스트 오버라이드 타입 ─────────────────────────────────────────────────

export interface VideoQualityOverride {
  grid: VideoQualityLevel | null;
  presenterMain: VideoQualityLevel | null;
  presenterSub: VideoQualityLevel | null;
  sideMain: VideoQualityLevel | null;
  sideSub: VideoQualityLevel | null;
  /** 그리드뷰 임계값 정책 오버라이드 (null = 기본 정책 사용) */
  gridTiers: GridTier[] | null;
  gridFallback: VideoQualityLevel | null;
}

/** 드롭다운에서 선택 가능한 해상도 옵션 */
export const RESOLUTION_OPTIONS: readonly { label: string; value: VideoQualityLevel | null }[] = [
  { label: '자동 (정책 기본값)', value: null },
  { label: '720p (HD)', value: 720 },
  { label: '360p (Standard)', value: 360 },
  { label: '180p (Low)', value: 180 },
] as const;
